import type { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { LevelData, Point } from "../generation/types";
import { TileType, pointKey } from "../generation/types";
import { worldToGrid } from "../rendering/worldCoordinates";
import { createButton, createElement } from "./dom";
import type { GameState } from "../game/GameState";
import { exploredNearbyCells } from "./AutomapExploration";

export function gridToAutomapCell(point: Point, gridHeight: number): Point {
  return { x: point.x, y: gridHeight - 1 - point.y };
}

export class Automap {
  public readonly element = createElement("section", "automap hidden");
  private readonly canvas = createElement("canvas");
  private readonly title = createElement(
    "div",
    "automap-title",
    "ЛОКАЛЬНАЯ КАРТА // ИССЛЕДОВАННЫЕ СЕКТОРЫ",
  );
  private readonly visited = new Set<string>();
  private level?: LevelData;
  private debugOverlay = false;
  private readonly stats = createElement("div", "automap-stats");
  public onClose: () => void = () => undefined;

  public constructor() {
    this.canvas.width = 768;
    this.canvas.height = 768;
    this.element.append(
      this.title,
      this.canvas,
      this.stats,
      createButton("ЗАКРЫТЬ КАРТУ", () => this.onClose(), "map-close"),
      createElement("div", "automap-help", "КАРТА НЕ ОСТАНАВЛИВАЕТ БОЙ"),
    );
  }

  public setStats(state: GameState): void {
    this.stats.textContent = `ВРАГИ ${state.kills} / ${state.totalEnemies}   ·   ТАЙНИКИ ${state.secrets} / ${state.totalSecrets}`;
  }

  public setLevel(level: LevelData): void {
    this.level = level;
    this.visited.clear();
    this.setDebugOverlay(false);
  }

  public setDebugOverlay(enabled: boolean): void {
    this.debugOverlay = enabled;
    this.title.textContent = enabled
      ? this.level?.mode === "procedural"
        ? "ПОЛНАЯ КАРТА СПУСКА // СЕТКА 64×64"
        : "ОТЛАДОЧНАЯ КАРТА МИССИИ // ФИКСИРОВАННАЯ МАКРОТОПОЛОГИЯ"
      : "ЛОКАЛЬНАЯ КАРТА // ИССЛЕДОВАННЫЕ СЕКТОРЫ";
  }

  public visit(position: Vector3): void {
    const level = this.level;
    if (!level) return;
    const cell = worldToGrid(position.x, position.z, level.grid);
    for (const key of exploredNearbyCells(level.grid, cell)) this.visited.add(key);
  }

  public draw(playerPosition: Vector3): void {
    const level = this.level;
    const context = this.canvas.getContext("2d");
    if (!level || !context) return;
    context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    context.fillStyle = "#050a08";
    context.fillRect(0, 0, this.canvas.width, this.canvas.height);
    const cellSize = this.canvas.width / level.grid.width;
    for (let y = 0; y < level.grid.height; y += 1) {
      for (let x = 0; x < level.grid.width; x += 1) {
        const point = { x, y };
        if (!this.debugOverlay && !this.visited.has(pointKey(point))) continue;
        if (this.debugOverlay && !level.grid.isWalkable(point)) continue;
        const tile = level.grid.get(point);
        const mapPoint = gridToAutomapCell(point, level.grid.height);
        context.fillStyle =
          tile === TileType.Toxic ? "#517d35" : tile >= TileType.Door ? "#d08732" : "#30483f";
        context.fillRect(
          mapPoint.x * cellSize,
          mapPoint.y * cellSize,
          Math.ceil(cellSize),
          Math.ceil(cellSize),
        );
        this.drawBoundary(context, level, point, cellSize);
      }
    }
    const exitVisible = this.debugOverlay || this.visited.has(pointKey(level.exit));
    if (exitVisible) {
      const exit = gridToAutomapCell(level.exit, level.grid.height);
      context.fillStyle = "#e3a23e";
      context.fillRect(exit.x * cellSize - 3, exit.y * cellSize - 3, cellSize + 6, cellSize + 6);
    }
    if (this.debugOverlay && level.mission) this.drawMissionOverlay(context, level, cellSize);
    const player = worldToGrid(playerPosition.x, playerPosition.z, level.grid);
    const mapPlayer = gridToAutomapCell(player, level.grid.height);
    context.fillStyle = "#63e6c5";
    context.beginPath();
    context.arc((mapPlayer.x + 0.5) * cellSize, (mapPlayer.y + 0.5) * cellSize, 6, 0, Math.PI * 2);
    context.fill();
  }

  public show(playerPosition: Vector3): void {
    this.draw(playerPosition);
    this.element.classList.remove("hidden");
  }

  public hide(): void {
    this.element.classList.add("hidden");
  }

  private drawBoundary(
    context: CanvasRenderingContext2D,
    level: LevelData,
    point: Point,
    size: number,
  ): void {
    context.strokeStyle = "#77a78e";
    context.lineWidth = 1;
    const mapPoint = gridToAutomapCell(point, level.grid.height);
    const x = mapPoint.x * size;
    const y = mapPoint.y * size;
    const edges: Array<{
      neighbor: Point;
      fromX: number;
      fromY: number;
      toX: number;
      toY: number;
    }> = [
      {
        neighbor: { x: point.x, y: point.y - 1 },
        fromX: x,
        fromY: y + size,
        toX: x + size,
        toY: y + size,
      },
      {
        neighbor: { x: point.x + 1, y: point.y },
        fromX: x + size,
        fromY: y,
        toX: x + size,
        toY: y + size,
      },
      {
        neighbor: { x: point.x, y: point.y + 1 },
        fromX: x,
        fromY: y,
        toX: x + size,
        toY: y,
      },
      { neighbor: { x: point.x - 1, y: point.y }, fromX: x, fromY: y, toX: x, toY: y + size },
    ];
    for (const edge of edges) {
      if (
        level.grid.get(edge.neighbor) !== TileType.SecretDoor &&
        level.grid.canTraverse(point, edge.neighbor)
      )
        continue;
      context.beginPath();
      context.moveTo(edge.fromX, edge.fromY);
      context.lineTo(edge.toX, edge.toY);
      context.stroke();
    }
  }

  private drawMissionOverlay(
    context: CanvasRenderingContext2D,
    level: LevelData,
    size: number,
  ): void {
    const mission = level.mission;
    if (!mission) return;
    context.save();
    context.lineWidth = 2;
    context.font = "10px monospace";
    context.textBaseline = "top";
    for (const landmark of mission.landmarks) {
      const topLeft = gridToAutomapCell(
        { x: landmark.bounds.x, y: landmark.bounds.y + landmark.bounds.height - 1 },
        level.grid.height,
      );
      context.strokeStyle = "rgba(205, 150, 82, 0.48)";
      context.strokeRect(
        topLeft.x * size,
        topLeft.y * size,
        landmark.bounds.width * size,
        landmark.bounds.height * size,
      );
      const label = gridToAutomapCell(landmark.center, level.grid.height);
      context.fillStyle = "#d4aa72";
      context.fillText(landmark.name.toUpperCase(), label.x * size + 4, label.y * size + 4);
    }
    for (const gate of mission.gates) {
      context.fillStyle =
        gate.requiredKey === "blue"
          ? "#4ba7de"
          : gate.requiredKey === "yellow"
            ? "#dfbd42"
            : "#dc4a32";
      for (const cell of gate.cells) {
        const point = gridToAutomapCell(cell, level.grid.height);
        context.fillRect(point.x * size, point.y * size, size, size);
      }
    }
    context.restore();
  }
}
